import {useAuth} from "../../../../context";
import {useEffect, useState} from "react";
import {fetchWrapper} from "../../../../_services/fetch";
import {config} from "../../../../utils2";
import {useSearchParams} from "react-router-dom";
import * as yup from "yup";

const api = config.get_remote_api() + "/api/tags"

const endpointOrderOptions = () => `${api}/order`
const endpointDetails = (userId, tagId) => `${api}/${userId}/${tagId}`
const endpointCount = (userId, pub, search) => `${api}/count/${userId}/${pub}${fetchWrapper.getSearchParams(search)}`
const endpointList = (userId, pub, search) => `${api}/list/${userId}/${pub}${fetchWrapper.getSearchParams(search)}`

const endpointAdd = (userId) => `${api}/${userId}`
const endpointEdit = (userId, targetId) => `${api}/${userId}/${targetId}`
const endpointDelete = (userId, targetId) => `${api}/${userId}/${targetId}`

const msg = "Failed with no error messages from the server."
const handleSuccess = resp => resp.success ? Promise.resolve(resp?.data) : Promise.reject({message: resp.message || msg})
const handleError = err => err?.name !== "AbortError" ? err?.error || err?.message || msg : null


export function useTag(userId, tid = null) {
    const defaultValues = {
        id: null,
        name: "",
        variant: "",
        public: true,
        created_at: null,
        updated_at: null,
    }

    const validationSchema = yup.object().shape({
        name: yup.string().required("Name cannot be empty").max(32),
        variant: yup.string().required('Variant is required'),
    })

    const {token} = useAuth()
    const {value, setValue, loadingValue, setLoadingValue, errorValue, setErrorValue} = useTagDetails(tid, defaultValues)

    function handleSuccessInt(resp) {
        if (resp.success) {
            setValue(resp?.data ? resp.data : defaultValues);
            return Promise.resolve(resp.message);
        } else {
            return Promise.reject({message: resp?.message || "Fetch failed with no error messages from the server."});
        }
    }

    const handleErrorInt = err => {
        setErrorValue(handleError(err))
        return Promise.reject(handleError(err))
    }


    function addItem(formValue) {
        const {id, ...valuesNoId} = formValue
        return fetchWrapper.post(endpointAdd(userId), valuesNoId, token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function editItem(formValue, targetId = tid) {
        return fetchWrapper.put(endpointEdit(userId, targetId), formValue, token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function deleteItem(targetId = tid) {
        return fetchWrapper.delete(endpointDelete(userId, targetId), token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }


    return {
        value,
        validationSchema,
        addItem,
        editItem,
        deleteItem,
        loadingValue,
        errorValue,
        clearErrorValue: () => setErrorValue(null)
    }
}

export function useTagDetails(tagId, defaultValue) {

    const {loggedIn, userId, token} = useAuth()
    const [loadingValue, setLoadingValue] = useState(false);
    const [errorValue, setErrorValue] = useState(null);
    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn && tagId && tagId !== "add") {
            setErrorValue(null)
            setLoadingValue(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpointDetails(userId, tagId), requestOptions)
                .then(handleSuccess)
                .then(setValue)
                .catch(err => setErrorValue(handleError(err)))
                .finally(() => setLoadingValue(false))
        }

        return () => {
            controller.abort()
            setLoadingValue(false)
        }
    }, [loggedIn, tagId]);


    return {
        value,
        setValue,
        loadingValue,
        setLoadingValue,
        errorValue,
        setErrorValue,
        clearError: () => setErrorValue(null)
    }
}

export function useTagCount(queryOption = null) {

    const {loggedIn, userId, token} = useAuth()
    const [searchParams] = useSearchParams()
    const [loadingCount, setLoadingCount] = useState(false);
    const [errorCount, setErrorCount] = useState(null);
    const [count, setCount] = useState(0);

    // Search options
    const query = queryOption || searchParams.get("query")

    const media =searchParams.get("media")
    const product =searchParams.get("product")
    const device =searchParams.get("device")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorCount(null)
            setLoadingCount(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {query, media, product, device}

            fetchWrapper.generic(endpointCount(userId, "true", search), requestOptions)
                .then(handleSuccess)
                .then(setCount)
                .catch(err => setErrorCount(handleError(err)))
                .finally(() => setLoadingCount(false))
        }

        return () => {
            controller.abort()
            setLoadingCount(false)
        }
    }, [loggedIn, query, media, product, device]);

    return {
        count,
        setCount,
        loadingCount,
        errorCount,
        clearErrorCount: () => setErrorCount(null)
    }
}

export function useTagList(pageSizeOption = null, pageIndexOption = null, queryOption = null, orderByOption = null, keepPrev = false) {

    const defaultPageSize = 25
    const defaultPageIndex = 0

    const {loggedIn, userId, token} = useAuth()
    const [searchParams, setSearchParams] = useSearchParams()
    const [loadingList, setLoadingList] = useState(false);
    const [errorList, setErrorList] = useState(null);
    const [list, setList] = useState([]);
    const [reload, setReload] = useState(0);

    // Search options
    const pageSize = pageSizeOption || Number(searchParams.get("size")) || defaultPageSize
    const pageIndex = pageIndexOption || Number(searchParams.get("index")) || defaultPageIndex
    const query = queryOption || searchParams.get("query")
    const orderBy = orderByOption || searchParams.get("by")

    const media =searchParams.get("media")
    const product =searchParams.get("product")
    const device =searchParams.get("device")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn && pageIndex != null && pageSize != null) {
            setErrorList(null)
            setLoadingList(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {
                offset: pageIndex * pageSize,
                limit: pageSize,
                query: query,
                order_by: orderBy,
                media: media,
                product: product,
                device: device,
            }

            fetchWrapper.generic(endpointList(userId, "true", search), requestOptions)
                .then(handleSuccess)
                .then(data => setList(prev => keepPrev ? ([...prev, ...data]) : data))
                .catch(err => setErrorList(handleError(err)))
                .finally(() => setLoadingList(false))
        }

        return () => {
            controller.abort()
            setLoadingList(false)
        }
    }, [loggedIn, pageIndex, pageSize, query, orderBy, reload, media, product, device]);

    return {
        list,
        setList: fields => {
            setList(prev => {
                const fieldsNew = (typeof fields === "function") ? fields(prev) : fields
                if (fieldsNew.length === 0 && pageIndex > 0) {
                    setSearchParams(prev => {
                        prev.set("index", `${pageIndex - 1}`)
                    })
                } else if (fieldsNew.length < prev.length) {
                    setReload(reload + 1)
                }
                return [...fieldsNew]
            })
        },
        loadingList,
        errorList,
        clearErrorList: () => setErrorList(null)
    }
}

export function useTagOrderOptions(defaultOptions = []) {

    const {loggedIn, userId, token} = useAuth()
    const [loadingOrderOptions, setLoadingOrderOptions] = useState(false);
    const [errorOrderOptions, setErrorOrderOptions] = useState(null);
    const [orderOptions, setOrderOptions] = useState(defaultOptions);

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorOrderOptions(null)
            setLoadingOrderOptions(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpointOrderOptions(userId), requestOptions)
                .then(handleSuccess)
                .then(setOrderOptions)
                .catch(err => setErrorOrderOptions(handleError(err)))
                .finally(() => setLoadingOrderOptions(false))
        }

        return () => {
            controller.abort()
            setLoadingOrderOptions(false)
        }
    }, [loggedIn]);


    return {
        orderOptions,
        setOrderOptions,
        loadingOrderOptions,
        setLoadingOrderOptions,
        errorOrderOptions,
        setErrorOrderOptions,
        clearErrorOrderOptions: () => setErrorOrderOptions(null)
    }
}